// Tributaries use the same world-space water strip as scenery footing checks. Their bitmap is painted into a cached
// ground tile; no geometry, colour, or texture depends on that tile's origin, so neighbouring tiles and world wraps join.
import { cellHash, wrap } from "./geo";
import { materialDelta, type Material, type TerrainMaterials } from "./materials";
import { riverWaterStrength, type RiverSeg } from "./rivers";

/** The wet gravel extends only this far outside the measured water, in world cells. */
const BANK = 0.035;
const BANK_DRAWN = BANK;
const WATER = [42, 70, 82] as const;
const SHALLOWS = [80, 108, 106] as const;
const GRAVEL = [84, 81, 65] as const;
const ZERO = [0, 0, 0] as const;
const WATER_DELTA = [0, 0, 0];
const SHORE_DELTA = [0, 0, 0];
type Pixel = [number, number, number, number];

interface RiverTexture {
  water: Material | null;
  shore: Material | null;
  strength: number;
  /** painted world v2 (Oct 15 2026): the painted stream water, deep water and gravel, sampled for their own colours */
  pw?: PaintedRiver | null;
}
export interface PaintedRiver {
  water: Material;
  deep: Material;
  gravel: Material;
}
/** the painted bank: a little wider gravel than the drawn one, so a stream reads as set in its own bed */
export const PAINTED_BANK = 0.06;
const PW_A = [0, 0, 0];
const PW_B = [0, 0, 0];
const PW_C = [0, 0, 0];

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
function smooth(lo: number, hi: number, v: number): number {
  const t = clamp((v - lo) / (hi - lo), 0, 1);
  return t * t * (3 - 2 * t);
}

function distanceToSegment(x: number, y: number, segment: RiverSeg): number {
  const vx = segment.bx - segment.ax, vy = segment.by - segment.ay;
  const t = clamp(((x - segment.ax) * vx + (y - segment.ay) * vy) / (vx * vx + vy * vy || 1), 0, 1);
  return Math.hypot(x - segment.ax - vx * t, y - segment.ay - vy * t);
}

/** Water only: banks never exclude scenery. Widths are half widths, and overlaps retain the strongest water strip. */
export function riverFieldAt(segments: readonly RiverSeg[], x: number, y: number): number {
  let strength = 0;
  for (const segment of segments) {
    if (segment.width <= 0) continue;
    strength = Math.max(strength, riverWaterStrength(distanceToSegment(x, y, segment), segment.width));
    if (strength === 1) break;
  }
  return strength;
}

/** Continuous fine grain, with an integer number of samples around either world axis. */
function grainAt(x: number, y: number, size: number, frequency: number, salt: number): number {
  const fx = wrap(x, size) * frequency, fy = wrap(y, size) * frequency;
  const ix = Math.floor(fx), iy = Math.floor(fy), period = size * frequency;
  const tx = smooth(0, 1, fx - ix), ty = smooth(0, 1, fy - iy);
  const at = (dx: number, dy: number) => cellHash(wrap(ix + dx, period), wrap(iy + dy, period), salt);
  return mix(mix(at(0, 0), at(1, 0), tx), mix(at(0, 1), at(1, 1), tx), ty);
}

/** A pure stream sample, also used to check the inlet and coverage contract without a browser canvas. Materials only
 *  vary its colour. At a lake mouth the gravel disappears before the lake's water edge (0.47), while the stream remains
 *  opaque through that edge and fades only after entering the lake's open water. */
export function riverPixel(distance: number, halfWidth: number, lake: number, x: number, y: number, size: number,
  detail: number, tex?: RiverTexture, out: Pixel = [0, 0, 0, 0]): Pixel | null {
  const BANKW = tex?.pw ? PAINTED_BANK : BANK;
  if (halfWidth <= 0 || distance >= halfWidth + BANKW) return null;
  const wx = wrap(x, size), wy = wrap(y, size);
  const gravelGrain = grainAt(wx, wy, size, 7, 1423);
  const bankWidth = BANKW * (0.68 + 0.32 * gravelGrain);
  const bank = 0.58 * (1 - smooth(halfWidth * 0.9, halfWidth + bankWidth, distance)) * (1 - smooth(0.2, 0.3, lake));
  const strength = riverWaterStrength(distance, halfWidth);
  const water = strength * (1 - smooth(0.65, 1.2, lake));
  const gravel = bank * (1 - water);
  const alpha = water + gravel;
  if (alpha <= 0) return null;

  if (tex?.pw) {
    // painted: the light stream water deepening to the lake's water in the core, set in damp painted gravel
    const p = tex.pw;
    const sw = materialDelta(p.water, wx, wy, PW_A), dw2 = materialDelta(p.deep, wx, wy, PW_B), gv = materialDelta(p.gravel, wx, wy, PW_C);
    // deeper in the core, and the lake's own water as the stream runs into it (no lighter line across the lake)
    const depth = Math.max(smooth(0.15, 0.95, strength) * 0.55, smooth(0.42, 0.85, lake));
    const wet = mix(0.6, 0.85, smooth(halfWidth, halfWidth + bankWidth, distance));
    for (let i = 0; i < 3; i++) out[i] = (mix(sw[i], dw2[i], depth) * water + gv[i] * wet * gravel) / alpha;
    out[3] = alpha;
    return out;
  }
  const tk = tex?.strength ?? 0;
  const dw = water > 0 && tk > 0 && tex?.water ? materialDelta(tex.water, wx, wy, WATER_DELTA) : ZERO;
  const ds = gravel > 0 && tk > 0 && tex?.shore ? materialDelta(tex.shore, wx, wy, SHORE_DELTA) : ZERO;
  const depth = smooth(0.05, 0.75, strength);
  const waterGrain = water > 0 ? (grainAt(wx, wy, size, 17, 1429) - 0.5) * 6 * detail : 0;
  const grit = (gravelGrain - 0.5) * 14 * detail;
  let glint = 0, facet = 0;
  if (detail > 0 && water > 0 && strength > 0.55) {
    // Fine crests are broken into short dashes, confined to the core. Integer world frequencies keep both seams joined;
    // their small luminance lift preserves the blue-green water rather than adding a pale stripe along its edge.
    const phase = wy * 7 + (grainAt(wx, wy, size, 3, 1433) - 0.5) * 0.55;
    const crest = smooth(0.72, 0.98, Math.sin(phase * Math.PI * 2));
    const dash = smooth(0.45, 0.67, grainAt(wx, wy, size, 6, 1439));
    glint = crest * dash * smooth(0.55, 0.95, strength) * 18 * detail;
  }
  if (detail > 0 && gravel > 0) {
    // Tiny tonal steps suggest individual wet gravel faces; the existing bank alpha alone controls their footprint.
    facet = (Math.floor(grainAt(wx, wy, size, 19, 1447) * 4) / 3 - 0.5) * 24 * detail;
  }
  for (let i = 0; i < 3; i++) {
    const wc = mix(SHALLOWS[i], WATER[i], depth) + waterGrain + glint + dw[i] * tk * 0.35;
    const gc = GRAVEL[i] + grit + facet + ds[i] * tk * 0.45;
    out[i] = (wc * water + gc * gravel) / alpha;
  }
  out[3] = alpha;
  return out;
}

/** Paint provided river spans into this ground tile. Raster work is bounded to each segment's strip; lake fields and
 *  material samples are evaluated only within that strip, never across all of a tile or through RiverNetwork queries. */
export function paintRivers(g: CanvasRenderingContext2D, x0: number, top: number, R: number, s: number, size: number,
  alpha: number, segments: readonly RiverSeg[], lakeField: (x: number, y: number) => number, mats?: TerrainMaterials, pw?: PaintedRiver | null): void {
  if (alpha <= 0 || R <= 0 || s <= 0 || !segments.length) return;
  const BANK = pw ? PAINTED_BANK : BANK_DRAWN;
  const intersects = (segment: RiverSeg) => segment.width > 0 &&
    Math.max(segment.ax, segment.bx) + segment.width + BANK >= x0 &&
    Math.min(segment.ax, segment.bx) - segment.width - BANK <= x0 + R &&
    Math.max(segment.ay, segment.by) + segment.width + BANK >= top - R &&
    Math.min(segment.ay, segment.by) - segment.width - BANK <= top;
  if (!segments.some(intersects)) return;

  const K = clamp(Math.round(s / 1.5), 4, 64);
  const N = Math.max(1, Math.round(R * K)), samplesPerCell = N / R;
  // Below 1 this is distance / halfWidth, so the minimum is exactly the strongest riverWaterStrength. Outside the
  // water it is 1 + distance-to-bank / BANK; this preserves the union even when adjoining strips have different widths.
  const D = new Float32Array(N * N).fill(Infinity);
  const W = new Float32Array(N * N);
  const nearest = new Uint32Array(N * N);
  let anySample = false;
  let left = N, right = -1, north = N, south = -1;
  for (let si = 0; si < segments.length; si++) {
    const segment = segments[si];
    if (!intersects(segment)) continue;
    const reach = segment.width + BANK;
    const minx = Math.min(segment.ax, segment.bx) - reach, maxx = Math.max(segment.ax, segment.bx) + reach;
    const miny = Math.min(segment.ay, segment.by) - reach, maxy = Math.max(segment.ay, segment.by) + reach;
    const i0 = Math.max(0, Math.floor((minx - x0) * samplesPerCell));
    const i1 = Math.min(N - 1, Math.ceil((maxx - x0) * samplesPerCell));
    const j0 = Math.max(0, Math.floor((top - maxy) * samplesPerCell));
    const j1 = Math.min(N - 1, Math.ceil((top - miny) * samplesPerCell));
    const vx = segment.bx - segment.ax, vy = segment.by - segment.ay;
    const vv = vx * vx + vy * vy || 1;
    for (let j = j0; j <= j1; j++) {
      const y = top - (j + 0.5) / samplesPerCell;
      for (let i = i0; i <= i1; i++) {
        const x = x0 + (i + 0.5) / samplesPerCell;
        const t = clamp(((x - segment.ax) * vx + (y - segment.ay) * vy) / vv, 0, 1);
        const distance = Math.hypot(x - segment.ax - vx * t, y - segment.ay - vy * t);
        if (distance >= reach) continue;
        const measure = distance <= segment.width ? distance / segment.width : 1 + (distance - segment.width) / BANK;
        const o = j * N + i;
        if (measure >= D[o]) continue;
        D[o] = measure;
        W[o] = segment.width;
        nearest[o] = si;
        anySample = true;
        left = Math.min(left, i); right = Math.max(right, i);
        north = Math.min(north, j); south = Math.max(south, j);
      }
    }
  }
  if (!anySample) return;

  const pixels = new Uint8ClampedArray(N * N * 4);
  const detail = smooth(14, 56, s);
  const tex: RiverTexture | undefined = pw
    ? { water: null, shore: null, strength: detail, pw }
    : mats && (mats.lakeWater || mats.lakeShore) ? { water: mats.lakeWater, shore: mats.lakeShore, strength: detail } : undefined;
  const colour: Pixel = [0, 0, 0, 0];
  let anyPaint = false;
  for (let j = north; j <= south; j++) {
    const y = top - (j + 0.5) / samplesPerCell;
    for (let i = left; i <= right; i++) {
      const o = j * N + i;
      if (!Number.isFinite(D[o])) continue;
      const x = x0 + (i + 0.5) / samplesPerCell;
      const distance = D[o] <= 1 ? D[o] * W[o] : W[o] + (D[o] - 1) * BANK;
      let lakeX = x, lakeY = y;
      if (D[o] >= 1) {
        // Bank-only samples take their inlet suppression from the nearest water point. All callback coordinates remain
        // inside active water, and a bank cannot form an earthy barrier across the river's mouth.
        const segment = segments[nearest[o]];
        const vx = segment.bx - segment.ax, vy = segment.by - segment.ay;
        const t = clamp(((x - segment.ax) * vx + (y - segment.ay) * vy) / (vx * vx + vy * vy || 1), 0, 1);
        lakeX = segment.ax + vx * t; lakeY = segment.ay + vy * t;
      }
      const px = riverPixel(distance, W[o], lakeField(lakeX, lakeY), x, y, size, detail, tex, colour);
      if (!px || px[3] < 1 / 255) continue;
      const q = o * 4;
      pixels[q] = px[0]; pixels[q + 1] = px[1]; pixels[q + 2] = px[2]; pixels[q + 3] = px[3] * 255;
      anyPaint = true;
    }
  }
  if (!anyPaint) return;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = N;
  try {
    const cg = canvas.getContext("2d");
    if (!cg) return;
    const image = cg.createImageData(N, N);
    image.data.set(pixels);
    cg.putImageData(image, 0, 0);
    g.save();
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = "medium";
    g.globalAlpha = alpha;
    g.drawImage(canvas, 0, 0, R * s, R * s);
    g.restore();
  } finally {
    canvas.width = canvas.height = 0;
  }
}
